import { useState, useEffect } from "react";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { useSharePoint } from "./hooks/useSharePoint";
import { useStatusView } from "./hooks/useStatusView";
import LoginPage from "./components/LoginPage";
import POList from "./components/POList";
import POGrid from "./components/POGrid";
import StatusView from "./components/StatusView";
import DetailPanel from "./components/DetailPanel";
import MoveModal from "./components/MoveModal";
import UploadModal from "./components/UploadModal";
import AddVendorModal from "./components/AddVendorModal";
import "./styles/app.css";

function initials(name) {
  if (!name) return "?";
  return name.split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase();
}

const STATUSES = ["Ordered", "Partially Received", "Received", "Closed / Invoiced"];
const STATUS_STYLES = {
  "Ordered":             { color: "#5B3FD6", bg: "#F1EEFE" },
  "Partially Received":  { color: "#9A6B0C", bg: "#FEF3DC" },
  "Received":            { color: "#137A4D", bg: "#E3F7EC" },
  "Closed / Invoiced":   { color: "#4B5058", bg: "#ECEDEE" },
};

export default function App() {
  const isAuthenticated = useIsAuthenticated();
  const { instance } = useMsal();

  const {
    vendors, pos, comments, selectedVendor, setSelectedVendor,
    selectedPO, setSelectedPO, loading, error,
    handleUpload, handleMove, handlePostComment, handleStatusChange,
    handleRefresh, handleAddVendor, searchResults, searchScope,
    setSearchScope, searching, handleSearch, driveId, account,
  } = useSharePoint();

  const { statusPOs, loading: statusLoading, counts, fetchByStatus, fetchCounts } = useStatusView(driveId);

  const [search, setSearch] = useState("");
  const [movingPO, setMovingPO] = useState(null);
  const [showUpload, setShowUpload] = useState(false);
  const [showAddVendor, setShowAddVendor] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [viewMode, setViewMode] = useState("list");
  const [activeStatusView, setActiveStatusView] = useState("Ordered");

  useEffect(() => {
    if (driveId) fetchCounts();
  }, [driveId, fetchCounts]);

  useEffect(() => {
    if (!isAuthenticated || (!selectedVendor && !activeStatusView)) return;
    const interval = setInterval(() => {
      if (selectedPO || movingPO || showUpload || showAddVendor) return;
      if (activeStatusView) {
        fetchByStatus(activeStatusView);
        fetchCounts();
      } else {
        handleRefresh && handleRefresh();
        fetchCounts();
      }
    }, 30000);
    return () => clearInterval(interval);
  }, [isAuthenticated, selectedVendor, activeStatusView, selectedPO, movingPO, showUpload, showAddVendor, handleRefresh, fetchByStatus, fetchCounts]);

  useEffect(() => {
    if (!search.trim()) return;
    const t = setTimeout(() => handleSearch(search), 350);
    return () => clearTimeout(t);
  }, [search, searchScope, handleSearch]);

  useEffect(() => {
    if (activeStatusView && driveId) fetchByStatus(activeStatusView);
  }, [activeStatusView, driveId, fetchByStatus]);

  if (!isAuthenticated) return <LoginPage />;

  const selectVendor = (name) => {
    setSelectedVendor(name);
    setActiveStatusView(null);
    setSearch("");
    setSidebarOpen(false);
  };

  const selectStatusView = (status) => {
    setActiveStatusView(status);
    setSelectedVendor(null);
    setSearch("");
    setSidebarOpen(false);
    fetchByStatus(status);
  };

  const visibleVendors = vendors;
  const currentPos = search.trim() ? searchResults : activeStatusView ? statusPOs : pos;
  const currentLoading = search.trim() ? searching : activeStatusView ? statusLoading : loading.pos;

  // Status change handler that also refreshes search results if active
  const handleStatusChangeWithRefresh = async (fileId, newStatus) => {
    await handleStatusChange(fileId, newStatus);
    if (search.trim()) handleSearch(search);
  };

  return (
    <div className="app-shell">
      <aside className={`sidebar${sidebarOpen ? " sidebar-open" : ""}`}>
        <div className="sidebar-brand">
          <h1>PO Manager</h1>
          <p>Purchase orders · SharePoint</p>
          <button className="sidebar-close" onClick={() => setSidebarOpen(false)} aria-label="Close menu">
            <i className="ti ti-x" aria-hidden="true" />
          </button>
        </div>

        <div className="sidebar-search">
          <div className="search-input">
            <i className="ti ti-search search-icon" aria-hidden="true" />
            <input type="text" placeholder="Search names + contents…" value={search} onChange={(e) => setSearch(e.target.value)} />
            {searching && <div className="spinner search-spinner" />}
          </div>
          <div className="search-scope">
            <button className={`scope-btn${searchScope === "vendor" ? " active" : ""}`} onClick={() => setSearchScope("vendor")} disabled={!selectedVendor}>This vendor</button>
            <button className={`scope-btn${searchScope === "all" ? " active" : ""}`} onClick={() => setSearchScope("all")}>All vendors</button>
          </div>
        </div>

        <div className="sidebar-section">Status views</div>
        {STATUSES.map((s) => {
          const style = STATUS_STYLES[s];
          return (
            <button key={s} className={`vendor-btn${activeStatusView === s ? " active" : ""}`} onClick={() => selectStatusView(s)}>
              <div style={{ width: 9, height: 9, borderRadius: "50%", background: style.color, flexShrink: 0 }} />
              <span className="v-name">{s}</span>
              <span className="v-count" style={activeStatusView === s ? { background: style.bg, color: style.color } : {}}>
                {counts[s] ?? "…"}
              </span>
            </button>
          );
        })}

        <div className="sidebar-section" style={{ marginTop: 6 }}>
          Vendors
          <button className="add-vendor-btn" onClick={() => setShowAddVendor(true)} aria-label="Add vendor" title="Add vendor">
            <i className="ti ti-plus" aria-hidden="true" />
          </button>
        </div>

        <div className="vendor-list">
          {loading.init ? (
            <div style={{ padding: "20px", display: "flex", justifyContent: "center" }}><div className="spinner" /></div>
          ) : (
            visibleVendors.map((v) => (
              <button key={v.id} className={`vendor-btn${selectedVendor === v.name ? " active" : ""}`} onClick={() => selectVendor(v.name)}>
                <i className="ti ti-building-store v-icon" aria-hidden="true" />
                <span className="v-name">{v.name}</span>
                <span className="v-count">{v.folder?.childCount ?? 0}</span>
              </button>
            ))
          )}
        </div>

        <div className="sidebar-footer">
          {account && (
            <div className="user-chip">
              <div className="user-avatar">{initials(account.name)}</div>
              <span className="user-name">{account.name}</span>
              <button className="logout-btn" onClick={() => instance.logoutPopup()} title="Sign out" aria-label="Sign out">
                <i className="ti ti-logout" aria-hidden="true" />
              </button>
            </div>
          )}
        </div>
      </aside>

      {sidebarOpen && <div className="sidebar-backdrop" onClick={() => setSidebarOpen(false)} />}

      <main className={`main${selectedPO ? " main-with-detail" : ""}`}>
        <div className="topbar">
          <button className="hamburger-btn" onClick={() => setSidebarOpen(true)} aria-label="Open vendor menu">
            <i className="ti ti-menu-2" aria-hidden="true" />
          </button>
          <span className="topbar-title">
            {search.trim() ? (
              <>Search results <span>{searchResults.length} found{searchScope === "all" ? " (all vendors)" : ` in ${selectedVendor || "..."}`}</span></>
            ) : activeStatusView ? (
              <>{activeStatusView} <span>{statusPOs.length} document{statusPOs.length !== 1 ? "s" : ""} across all vendors</span></>
            ) : (
              <>{selectedVendor || "Select a vendor"}{pos.length > 0 && <span>{pos.length} document{pos.length !== 1 ? "s" : ""}</span>}</>
            )}
          </span>
          <button className="btn" onClick={activeStatusView ? () => { fetchByStatus(activeStatusView); fetchCounts(); } : handleRefresh} disabled={(!selectedVendor && !activeStatusView) || loading.pos} title="Refresh" aria-label="Refresh">
            <i className={`ti ti-refresh${(loading.pos || statusLoading) ? " ti-spin" : ""}`} aria-hidden="true" />
          </button>
          <div className="view-toggle">
            <button className={`view-btn${viewMode === "grid" ? " active" : ""}`} onClick={() => setViewMode("grid")} title="Grid view" aria-label="Grid view"><i className="ti ti-layout-grid" aria-hidden="true" /></button>
            <button className={`view-btn${viewMode === "list" ? " active" : ""}`} onClick={() => setViewMode("list")} title="List view" aria-label="List view"><i className="ti ti-list" aria-hidden="true" /></button>
          </div>
          <button className="btn btn-primary" onClick={() => setShowUpload(true)} disabled={!selectedVendor}>
            <i className="ti ti-upload" aria-hidden="true" />
            <span className="btn-label">Upload PO</span>
          </button>
        </div>

        {error && <div className="error-banner"><i className="ti ti-alert-circle" aria-hidden="true" /><span>{error}</span></div>}

        <div className="list-wrap">
          {activeStatusView && !search.trim() ? (
            viewMode === "grid" ? (
              <POGrid pos={statusPOs} loading={statusLoading} selectedPO={selectedPO} driveId={driveId}
                onSelect={setSelectedPO} onMove={setMovingPO}
                onStatusChange={async (fileId, newStatus) => { await handleStatusChange(fileId, newStatus); fetchByStatus(activeStatusView); fetchCounts(); }}
                searchQuery="" isSearchResults={false} />
            ) : (
              <StatusView status={activeStatusView} pos={statusPOs} loading={statusLoading}
                onSelect={setSelectedPO} selectedPO={selectedPO}
                onStatusChange={async (fileId, newStatus) => { await handleStatusChange(fileId, newStatus); fetchByStatus(activeStatusView); fetchCounts(); }} />
            )
          ) : viewMode === "grid" ? (
            <POGrid pos={currentPos} loading={currentLoading} selectedPO={selectedPO} driveId={driveId}
              onSelect={setSelectedPO} onMove={setMovingPO}
              onStatusChange={handleStatusChangeWithRefresh}
              searchQuery="" isSearchResults={!!search.trim()} />
          ) : (
            <POList pos={currentPos} loading={currentLoading} selectedPO={selectedPO}
              onSelect={setSelectedPO} onMove={setMovingPO}
              onStatusChange={handleStatusChangeWithRefresh}
              searchQuery="" isSearchResults={!!search.trim()} />
          )}
        </div>

        <div className="statusbar">
          <span>
            {activeStatusView ? `${statusPOs.length} document${statusPOs.length !== 1 ? "s" : ""} · ${activeStatusView}`
              : selectedVendor ? `${pos.length} document${pos.length !== 1 ? "s" : ""} · ${selectedVendor}`
              : "No vendor selected"}
          </span>
          {selectedPO && <span>Selected: {selectedPO.name}</span>}
        </div>
      </main>

      {selectedPO && (
        <DetailPanel po={selectedPO} comments={comments} loadingComments={loading.comments}
          onPostComment={handlePostComment}
          onStatusChange={(newStatus) => handleStatusChange(selectedPO.id, newStatus)}
          onClose={() => setSelectedPO(null)} driveId={driveId} />
      )}

      {movingPO && (
        <MoveModal po={movingPO} vendors={visibleVendors} currentVendor={selectedVendor}
          onConfirm={async (id, target) => { await handleMove(id, target); setMovingPO(null); }}
          onCancel={() => setMovingPO(null)} />
      )}

      {showUpload && <UploadModal vendorName={selectedVendor} onUpload={handleUpload} onCancel={() => setShowUpload(false)} />}
      {showAddVendor && <AddVendorModal existingVendors={vendors} onCreate={handleAddVendor} onCancel={() => setShowAddVendor(false)} />}
    </div>
  );
}
